// Synthèse TVA d'un mois à partir des écritures (10-12/10/2026) — ne
// remplit pas le formulaire CA3 : détail facture par facture, exports Excel
// et PDF pour l'expert-comptable.
import { useCallback, useEffect, useState } from "react";
import api from "../services/api";
import DolibarrConcordance from "./DolibarrConcordance";
import VatDeclarationPanel from "./VatDeclarationPanel";

const eur = (n) => `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const frDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");
const prevMonth = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };

function Table({ cols, rows }) {
  if (!rows.length) return <div style={{ fontSize: 12, color: "var(--text-muted)", padding: "6px 0" }}>Aucune ligne.</div>;
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="table" style={{ width: "100%", fontSize: 12 }}>
        <thead><tr>{cols.map(c => <th key={c.k} style={{ textAlign: c.num ? "right" : "left" }}>{c.l}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i}>{cols.map(c => <td key={c.k} style={{ textAlign: c.num ? "right" : "left" }}>{c.f ? c.f(r[c.k], r) : (r[c.k] ?? "—")}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

export default function SyntheseTVA({ currentCompanyId, onNavigate }) {
  const [month, setMonth] = useState(prevMonth());
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true); setError("");
    api.vatSummary.get(currentCompanyId, month).then(setData).catch(e => { setError(e.message); setData(null); }).finally(() => setLoading(false));
  }, [currentCompanyId, month]);
  useEffect(() => { load(); }, [load]);

  const exportFile = async (kind) => {
    try {
      const { blob, filename } = await (kind === "pdf" ? api.vatSummary.exportPdf : api.vatSummary.exportXlsx)(currentCompanyId, month);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { setError(e.message); }
  };

  if (!currentCompanyId) return <div className="alert info">Sélectionnez une société.</div>;
  const d = data;
  return (
    <div style={{ padding: "24px 28px", maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Synthèse TVA</h1>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="month" className="input" value={month} onChange={e => setMonth(e.target.value)} />
          <button className="btn" onClick={() => exportFile("xlsx")} disabled={!d}>⬇ Export Excel</button>
          <button className="btn" onClick={() => exportFile("pdf")} disabled={!d}>⬇ Export PDF</button>
        </div>
      </div>
      {d?.provisional && d.banner && (
        <div role="alert" style={{ background: "#fef3c7", color: "#92400e", padding: "10px 14px", borderRadius: 10, marginBottom: 14, fontSize: 13, fontWeight: 600 }}>
          ⚠️ {d.banner}
        </div>
      )}
      {(d?.notices || []).map((n, i) => (
        <div key={i} role="alert" style={{ background: "#fef3c7", color: "#92400e", padding: "8px 12px", borderRadius: 10, marginBottom: 10, fontSize: 12.5 }}>⚠️ {n}</div>
      ))}
      <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: 14 }}>
        Calculée à partir des écritures comptables. Document de travail à transmettre à votre expert-comptable : elle ne remplit pas le formulaire CA3.
      </div>
      {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>}
      {loading && <div style={{ color: "var(--text-muted)" }}>Calcul…</div>}
      {d && !loading && (
        <>
          <VatDeclarationPanel companyId={currentCompanyId} month={month} summary={d} onNavigate={onNavigate} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10, marginBottom: 18 }}>
            {[
              { l: "TVA collectée exigible", v: d.collected.total },
              { l: "Autoliquidation (TVA due)", v: d.autoliquidation.due },
              { l: "TVA déductible", v: d.totals.tva_deductible },
              d.totals.credit ? { l: "Crédit de TVA", v: d.totals.credit, c: "#16a34a" } : { l: "TVA à payer", v: d.totals.to_pay, c: "#b45309" },
            ].map(x => (
              <div key={x.l} className="card" style={{ padding: "12px 14px" }}>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{x.l}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: x.c || "var(--text)" }}>{eur(x.v)}</div>
              </div>
            ))}
          </div>

          <div className="card" style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>TVA collectée exigible par taux</div>
            <Table cols={[{ k: "rate", l: "Taux", f: v => `${v} %` }, { k: "base", l: "Base HT", num: 1, f: eur }, { k: "vat", l: "TVA", num: 1, f: eur }, { k: "count", l: "Factures", num: 1 }]} rows={d.collected.by_rate} />
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6 }}>
              Sur les encaissements, la TVA des prestations n'est exigible qu'au règlement (acomptes et créances d'ouverture compris). TVA encore en attente d'encaissement en fin de mois (445740) : {eur(d.pending_445740)}.
            </div>
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: "pointer", fontSize: 12.5 }}>Détail facture par facture ({d.collected.details.length})</summary>
              <Table cols={[
                { k: "number", l: "Facture" }, { k: "client", l: "Client" }, { k: "kind", l: "Type" }, { k: "invoice_date", l: "Date facture", f: frDate },
                { k: "mode", l: "Exigibilité" }, { k: "exigible_on", l: "Exigible le", f: v => String(v || "").split(", ").map(frDate).join(", ") },
                { k: "rate", l: "Taux", num: 1, f: v => `${v} %` }, { k: "base", l: "Base HT", num: 1, f: eur }, { k: "vat", l: "TVA", num: 1, f: eur },
              ]} rows={d.collected.details} />
            </details>
            {d.collected.other.length > 0 && (
              <div style={{ fontSize: 12, color: "#b45309", marginTop: 6 }}>⚠ {d.collected.other.length} écriture(s) de TVA collectée sans facture rattachée (à vérifier) : {eur(d.collected.other.reduce((s, x) => s + x.vat, 0))}.</div>
            )}
          </div>

          <div className="card" style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>TVA déductible</div>
            <Table cols={[{ k: "l", l: "Nature" }, { k: "v", l: "TVA", num: 1, f: eur }]} rows={[
              { l: "Biens (à la date de la facture)", v: d.deductible.biens.total },
              { l: "Immobilisations", v: d.deductible.immobilisations.total },
              { l: "Services — fournisseurs ayant opté pour les débits", v: d.deductible.services_debits?.total || 0 },
              { l: "Services payés dans le mois", v: d.deductible.services_paid.total },
              { l: "Autoliquidation", v: d.deductible.autoliquidation },
              { l: "Notes de frais (exclusions appliquées)", v: d.deductible.notes_de_frais?.total || 0 },
              { l: "Opérations bancaires sans facture (TVA saisie sur la ligne)", v: d.deductible.operations_bancaires?.total || 0 },
              { l: "Régularisations de périodes antérieures (mois déjà déclarés)", v: d.deductible.regularisations?.total || 0 },
              { l: "Total déductible", v: d.deductible.total },
            ]} />
            {(d.deductible.regularisations?.details || []).length > 0 && (
              <div style={{ fontSize: 12, marginTop: 6 }}>
                <div style={{ color: "var(--text-muted)", marginBottom: 4 }}>TVA déductible d'un mois déjà déclaré, reportée sur ce mois :</div>
                <Table cols={[
                  { k: "period", l: "Période d'origine", f: v => `${String(v).slice(5, 7)}/${String(v).slice(0, 4)}` }, { k: "nature", l: "Nature" }, { k: "number", l: "Pièce" },
                  { k: "vat", l: "TVA", num: 1, f: eur },
                ]} rows={d.deductible.regularisations.details} />
              </div>
            )}
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6 }}>
              Services en attente de paiement, non déduits : {eur(d.deductible.services_pending.total)} ({d.deductible.services_pending.details.length} facture(s)).
            </div>
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: "pointer", fontSize: 12.5 }}>Détail facture par facture</summary>
              <Table cols={[
                { k: "nature", l: "Nature", f: (v, r) => (r.nature_recorded === false ? `${v} *` : v) }, { k: "number", l: "Pièce" }, { k: "supplier", l: "Fournisseur" }, { k: "invoice_date", l: "Date facture", f: frDate },
                { k: "payment_date", l: "Payée le", f: frDate }, { k: "rate", l: "Taux", num: 1, f: v => `${v} %` }, { k: "ht", l: "HT", num: 1, f: eur }, { k: "vat", l: "TVA", num: 1, f: eur },
              ]} rows={[
                ...d.deductible.biens.details.map(x => ({ ...x, nature: "Biens" })),
                ...d.deductible.immobilisations.details.map(x => ({ ...x, nature: "Immobilisation" })),
                ...(d.deductible.services_debits?.details || []).map(x => ({ ...x, nature: "Service (option débits)" })),
                ...d.deductible.services_paid.details.map(x => ({ ...x, nature: "Service payé" })),
                ...d.deductible.services_pending.details.map(x => ({ ...x, nature: "Service en attente (non déduit)" })),
                ...(d.deductible.notes_de_frais?.details || []).map(x => ({ ...x, nature: "Note de frais" })),
                ...(d.deductible.operations_bancaires?.details || []).map(x => ({ ...x, nature: "Opération bancaire sans facture" })),
              ]} />
            </details>
          </div>

          <div className="card">
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Contrôles de cohérence avec les comptes</div>
            <Table cols={[
              { k: "label", l: "Contrôle" }, { k: "expected", l: "Comptes", num: 1, f: eur }, { k: "found", l: "Synthèse", num: 1, f: eur },
              { k: "ok", l: "", f: v => (v ? <span style={{ color: "#16a34a" }}>✓</span> : <span style={{ color: "#b45309" }}>écart</span>) }, { k: "explanation", l: "Explication" },
            ]} rows={d.controls} />
            {/* Écart expliqué pièce par pièce : comptes, synthèse, motif et ce qu'il y a à faire */}
            {d.controls.filter(c => !c.ok && c.details?.length).map((c, i) => (
              <details key={i} open style={{ marginTop: 10 }}>
                <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#b45309" }}>
                  Écart de {eur(Math.abs(c.gap))} sur « {c.label.split(" : ")[0]} » : {c.details.length} pièce(s) en cause
                </summary>
                <Table cols={[
                  { k: "piece", l: "Pièce" }, { k: "comptes", l: "Comptes", num: 1, f: eur }, { k: "synthese", l: "Synthèse", num: 1, f: eur },
                  { k: "ecart", l: "Écart", num: 1, f: eur }, { k: "motif", l: "Pourquoi" }, { k: "action", l: "À faire" },
                ]} rows={c.details} />
              </details>
            ))}
          </div>
        </>
      )}
      <DolibarrConcordance companyId={currentCompanyId} month={month} />
    </div>
  );
}
